import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    AppShell, Burger, Group, NavLink, Tooltip, rem, Menu, Text, ActionIcon, Divider, ScrollArea, Box, useMantineTheme, Indicator, Popover, ThemeIcon
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { 
  IconDashboard, IconUsers, IconReceipt, IconClipboardList, 
  IconLogout, IconWallet, IconFileInvoice, IconUserCircle, 
  IconAlertCircle, IconShieldLock, IconHistory, IconSettings,
  IconBell, IconInfoCircle, IconCash, 
  IconCloudUpload 
} from '@tabler/icons-react'; 
import io from 'socket.io-client';
import axios from '../../api/axios'; 
import Swal from 'sweetalert2';

const drawerWidth = 260;
const ENDPOINT = process.env.REACT_APP_API_URL || "http://localhost:5000";

const Layout = ({ children }) => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true); 
  const [menuOpened, { toggle: toggleMenu }] = useDisclosure(false); 

  // Notifications State
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpened, setNotifOpened] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));
  const role = user?.role;

  // --- 1. SOCKET CONNECTION & LISTENERS ---
  useEffect(() => {
    if (user) {
        const newSocket = io(ENDPOINT);
        setSocket(newSocket);

        // Join Rooms
        newSocket.emit('join_room', user._id); // Join personal room
        if (role === 'admin' || role === 'staff' || role === 'super_admin') {
            newSocket.emit('join_admin'); // Join admin room
        }

        // Listen for General Notifications
        newSocket.on('receive_notification', (notif) => {
            setNotifications((prev) => [notif, ...prev]);
            setUnreadCount((prev) => prev + 1);
            playNotificationSound();
        });

        // Listen for SPECIFIC POPUP ALERTS
        newSocket.on('new_ticket_popup', (data) => {
            Swal.fire({
                position: 'center',
                icon: 'warning',
                title: data.title,
                text: data.message,
                showConfirmButton: true,
                confirmButtonText: 'View Ticket',
                showCancelButton: true,
                timer: 8000 
            }).then((result) => {
                if (result.isConfirmed) navigate('/complaints');
            });
        });

        newSocket.on('payment_popup', (data) => {
            Swal.fire({
                position: 'center',
                icon: 'success',
                title: data.title,
                text: data.message,
                showConfirmButton: true,
                confirmButtonText: 'Review',
                showCancelButton: true,
                timer: 8000
            }).then((result) => {
                if (result.isConfirmed) navigate('/billing');
            });
        });

        fetchNotifications();

        return () => newSocket.disconnect();
    }
    // eslint-disable-next-line
  }, []);

  const fetchNotifications = async () => {
      try {
          const config = { headers: { Authorization: `Bearer ${user.token}` } };
          const { data } = await axios.get('/api/notifications', config);
          setNotifications(data);
          setUnreadCount(data.filter(n => !n.isRead).length);
      } catch (error) {
          console.error("Failed to fetch notifications");
      }
  };

  const playNotificationSound = () => {
      const audio = new Audio('/assets/notification.mp3'); 
      audio.play().catch(e => {}); 
  };

  const handleNotificationClick = async (notif) => {
      if (!notif.isRead) {
          const updated = notifications.map(n => n._id === notif._id ? { ...n, isRead: true } : n);
          setNotifications(updated);
          setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      if (notif.link) {
          navigate(notif.link);
          setNotifOpened(false);
      }
  };

  const markAllRead = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${user.token}` } };
        await axios.put('/api/notifications/read', {}, config);
        setUnreadCount(0);
        setNotifications(notifications.map(n => ({ ...n, isRead: true })));
      } catch (error) { console.error(error); }
  };

  // --- UPDATED LOGOUT FUNCTION ---
  const handleLogout = async () => {
    try {
        // Attempt to log the logout action on server
        if (user && user.token) {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            // We await this to ensure the log is created before we destroy the token
            await axios.post('/api/auth/logout', {}, config); 
        }
    } catch (error) {
        console.error("Logout logging failed:", error);
        // We proceed to logout locally even if server logging fails
    } finally {
        if(socket) socket.disconnect();
        localStorage.removeItem('user');
        navigate('/login');
    }
  };

  // --- MENU CONFIGURATION ---
  const residentMenu = [
    { label: 'My Dashboard', icon: IconDashboard, path: '/resident-dashboard' }, 
    { label: 'My Tickets & Requests', icon: IconClipboardList, path: '/resident-tickets' },
  ];

  const adminMenu = [
    { label: 'Dashboard', icon: IconDashboard, path: '/dashboard' },
    { label: 'Residents', icon: IconUsers, path: '/residents' },
    { label: 'Billing & Invoices', icon: IconReceipt, path: '/billing' },
    { label: 'Expenses', icon: IconWallet, path: '/expenses' },
    { label: 'Announcements', icon: IconAlertCircle, path: '/announcements' },
    { label: 'Ticket Management', icon: IconClipboardList, path: '/complaints' }, 
    { label: 'Reports', icon: IconFileInvoice, path: '/reports' },
  ];

  const superAdminMenu = [
    ...adminMenu, 
    { label: 'User Management', icon: IconShieldLock, path: '/user-management' },
    { label: 'Action Logs', icon: IconHistory, path: '/action-logs' },
    { label: 'Backup & Restore', icon: IconCloudUpload, path: '/backup' }, 
  ];

  let menuItems = [];
  if (role === 'resident') menuItems = residentMenu;
  else if (role === 'super_admin') menuItems = superAdminMenu;
  else menuItems = adminMenu;

  const mainLinks = menuItems.map((item) => (
    <Tooltip key={item.label} label={item.label} position="right" withArrow disabled={desktopOpened}>
      <NavLink
        component="button"
        label={item.label}
        leftSection={<item.icon style={{ width: rem(20), height: rem(20) }} />}
        active={location.pathname === item.path}
        onClick={() => navigate(item.path)}
        variant="subtle"
        color="green" 
        styles={() => ({
            root: {
                backgroundColor: location.pathname === item.path ? theme.colors.green[1] : 'transparent',
                color: location.pathname === item.path ? theme.colors.green[6] : theme.colors.gray[6],
                justifyContent: desktopOpened ? 'flex-start' : 'center',
            },
            label: {
                fontWeight: location.pathname === item.path ? 700 : 500,
                display: desktopOpened ? 'block' : 'none',
            },
            leftSection: { marginRight: desktopOpened ? rem(16) : 0 }
        })}
      />
    </Tooltip>
  ));

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: desktopOpened ? drawerWidth : 80,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        {/* Main Header Container with no-wrap to force alignment */}
        <Group h="100%" px="md" justify="space-between" bg={theme.colors.green[7]} wrap="nowrap">
          
          {/* LEFT SIDE: Burger and Title (Allowed to shrink) */}
          <Group gap="xs" wrap="nowrap" style={{ overflow: 'hidden', flex: 1 }}>
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" color="white" />
            <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="sm" size="sm" color="white" />
            
            {/* Title text with truncation */}
            <Text fw={700} size="lg" c="white" truncate>
              BALIHAI Management System
            </Text>
          </Group>

          {/* RIGHT SIDE: Icons (Priority - will not shrink) */}
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {/* NOTIFICATION BELL */}
            <Popover 
                width={300} 
                position="bottom-end" 
                withArrow 
                shadow="md"
                opened={notifOpened} 
                onChange={setNotifOpened}
            >
                <Popover.Target>
                    <ActionIcon 
                        variant="transparent" 
                        size="lg" 
                        color="white" 
                        onClick={() => setNotifOpened((o) => !o)}
                    >
                        <Indicator inline label={unreadCount} size={16} color="red" disabled={unreadCount === 0}>
                            <IconBell />
                        </Indicator>
                    </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown p={0}>
                    <Box p="xs" bg="gray.1" style={{ borderBottom: '1px solid #eee' }}>
                        <Group justify="space-between">
                            <Text size="sm" fw={700}>Notifications</Text>
                            <Text size="xs" c="blue" style={{ cursor: 'pointer' }} onClick={markAllRead}>
                                Mark all read
                            </Text>
                        </Group>
                    </Box>
                    <ScrollArea h={300}>
                        {notifications.length === 0 ? (
                            <Box p="md" ta="center">
                                <Text size="sm" c="dimmed">No notifications</Text>
                            </Box>
                        ) : (
                            notifications.map((n) => (
                                <Box 
                                    key={n._id} 
                                    p="sm" 
                                    style={{ 
                                        borderBottom: '1px solid #f1f1f1', 
                                        cursor: 'pointer',
                                        backgroundColor: n.isRead ? 'white' : '#e3f2fd' 
                                    }}
                                    onClick={() => handleNotificationClick(n)}
                                >
                                    <Group align="flex-start" wrap="nowrap">
                                        <ThemeIcon 
                                            color={n.type === 'complaint' ? 'orange' : n.type === 'payment' ? 'green' : 'blue'} 
                                            variant="light"
                                            size="sm"
                                        >
                                            {n.type === 'complaint' ? <IconAlertCircle size={14}/> : n.type === 'payment' ? <IconCash size={14}/> : <IconInfoCircle size={14}/>}
                                        </ThemeIcon>
                                        <Box>
                                            <Text size="sm" fw={n.isRead ? 400 : 600}>{n.title}</Text>
                                            <Text size="xs" c="dimmed">{n.message}</Text>
                                            <Text size="xs" c="dimmed" mt={4}>{new Date(n.createdAt).toLocaleString()}</Text>
                                        </Box>
                                    </Group>
                                </Box>
                            ))
                        )}
                    </ScrollArea>
                </Popover.Dropdown>
            </Popover>

            {/* User Menu */}
            <Menu shadow="md" width={200} position="bottom-end" opened={menuOpened} onChange={toggleMenu}>
                <Menu.Target>
                <ActionIcon variant="light" size="lg" radius="xl" color={theme.colors.green[2]}>
                    <IconUserCircle style={{ width: rem(24), height: rem(24) }} />
                </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                <Menu.Label>Signed in as:</Menu.Label>
                {/* Truncated user name in dropdown */}
                <Text size="sm" p="xs" truncate style={{ maxWidth: '180px' }}>
                    {user?.name} ({role?.replace('_', ' ').toUpperCase()})
                </Text>
                <Menu.Divider />
                <Menu.Item 
                    leftSection={<IconSettings style={{ width: rem(14), height: rem(14) }} />}
                    onClick={() => navigate('/profile')}
                >
                    Profile Settings
                </Menu.Item>
                <Menu.Item
                    color="red"
                    leftSection={<IconLogout style={{ width: rem(14), height: rem(14) }} />}
                    onClick={handleLogout}
                >
                    Logout
                </Menu.Item>
                </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <AppShell.Section mb="xl">
            <Group justify={desktopOpened ? 'flex-start' : 'center'} align="center" gap="sm">
                <Box style={{ width: rem(40), height: rem(40), overflow: 'hidden' }}>
                    <img src="/logo.png" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </Box>
                {desktopOpened && <Text fw={700} fz="md" c={theme.colors.green[6]}>BALIHAI</Text>}
            </Group>
        </AppShell.Section>

        <AppShell.Section grow component={ScrollArea}>
          {mainLinks}
        </AppShell.Section>

        <AppShell.Section>
            <Divider my="sm" />
            <NavLink
                label="Logout"
                leftSection={<IconLogout style={{ width: rem(20), height: rem(20) }} />}
                onClick={handleLogout}
                variant="subtle"
                color="red"
                styles={{
                    root: { justifyContent: desktopOpened ? 'flex-start' : 'center' },
                    label: { display: desktopOpened ? 'block' : 'none' },
                    leftSection: { marginRight: desktopOpened ? rem(16) : 0 },
                }}
            />
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
};

export default Layout;